import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { Theme } from "@mui/material/styles";
import { makeStyles, createStyles } from "@mui/styles";
import { Modal, Backdrop, Fade } from "@mui/material/";

// Redux
import { setModalIsOpened } from "@/main/store/modal";

// Utils
import { OnKeyPressEnum } from "@/main/services/enum/OnKeyPressEnum";

// Styles
import * as S from "./styles";
import { Z_INDEX_MODAL } from "@/main/services/constants";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		paper: {
			backgroundColor: theme.palette.background.paper,
			border: "2px solid #000",
			boxShadow: theme.shadows[5],
			padding: theme.spacing(2, 4, 3),
		},
	}),
);

interface IKogModalFadeComponent {
	open: boolean;
	setOpen: (open: boolean) => void;
	titleModalReducer?: string;
	title?: string | React.JSX.Element | undefined;
	isInModal?: boolean;
	children: React.ReactNode;
	closeByOverlay?: boolean;
	keepMounted?: boolean;
	hasBackgroundTitle?: boolean;
}

export default function KogModalFadeComponent({
	open,
	setOpen,
	title,
	titleModalReducer = "Modal Fade",
	isInModal = false,
	closeByOverlay = true,
	keepMounted = false,
	hasBackgroundTitle = false,
	children,
}: IKogModalFadeComponent) {
	const classes = useStyles();

	const dispatch = useDispatch();

	useEffect(() => {
		if (closeByOverlay) {
			const handleKeyDown = (event: KeyboardEvent) => {
				if (event.key === OnKeyPressEnum.Escape) setOpen(false);
			};

			document.addEventListener("keydown", handleKeyDown);

			return () => {
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [setOpen]);

	useEffect(() => {
		if (!isInModal) dispatch(setModalIsOpened(open, open ? titleModalReducer : null));
	}, [open]);

	const handleClose = (_event: unknown, _reason: "backdropClick" | "escapeKeyDown") => {
		if (closeByOverlay) {
			setOpen(false);
		}
	};
	return (
		<>
			{createPortal(
				<Modal
					id="id-general-modal"
					className={`${classes.modal} ignore-click`}
					open={open}
					onClose={handleClose}
					keepMounted={keepMounted}
					closeAfterTransition
					BackdropComponent={Backdrop}
					BackdropProps={{
						timeout: 500,
					}}
					autoFocus
				>
					<Fade in={open}>
						<div
							style={{
								zIndex: Z_INDEX_MODAL,
								maxWidth: "100%",
							}}
						>
							{title && <S.TitleModal hasBackgroundTitle={hasBackgroundTitle}>{title}</S.TitleModal>}
							{children}
						</div>
					</Fade>
				</Modal>,
				document.body,
			)}
		</>
	);
}
